/**
 * Tip güvenli event bus.
 * Sistemler birbirini doğrudan import etmek yerine buradan haberleşir;
 * böylece yeni sistem eklemek mevcut sistemleri değiştirmeyi gerektirmez.
 */
export type Listener<T> = (payload: T) => void;

export class EventBus<Events extends Record<string, unknown>> {
  private listeners = new Map<keyof Events, Set<Listener<never>>>();

  on<K extends keyof Events>(type: K, fn: Listener<Events[K]>): () => void {
    let set = this.listeners.get(type);
    if (!set) {
      set = new Set();
      this.listeners.set(type, set);
    }
    set.add(fn as Listener<never>);
    return () => this.off(type, fn);
  }

  once<K extends keyof Events>(type: K, fn: Listener<Events[K]>): () => void {
    const off = this.on(type, (p) => {
      off();
      fn(p);
    });
    return off;
  }

  off<K extends keyof Events>(type: K, fn: Listener<Events[K]>): void {
    this.listeners.get(type)?.delete(fn as Listener<never>);
  }

  emit<K extends keyof Events>(type: K, payload: Events[K]): void {
    const set = this.listeners.get(type);
    if (!set) return;
    // Kopya üzerinde gez: dinleyici içinde off/on çağrılırsa döngü bozulmasın.
    for (const fn of [...set]) {
      try {
        (fn as Listener<Events[K]>)(payload);
      } catch (err) {
        // Bir dinleyicinin hatası diğerlerini ve oyun döngüsünü düşürmesin.
        console.error(`[EventBus] "${String(type)}" dinleyicisi hata verdi:`, err);
      }
    }
  }

  clear(): void {
    this.listeners.clear();
  }
}
